import { useQuery } from "@tanstack/react-query";
import { CalendarRange, Download, ScatterChart as ScatterIcon, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  CartesianGrid,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  getDispersionHc,
  getOfertasCapacidadRango,
  getPeriodoHc,
  type DispersionHcFila,
} from "@/lib/api-mercado";

const SISTEMAS = ["SIN", "BCA", "BCS"] as const;
// Mismos colores por sistema que /margen-capacidad (la identidad la da el color).
const COLOR_SISTEMA: Record<string, string> = {
  SIN: "#1d4ed8",
  BCA: "#059669",
  BCS: "#d97706",
};
// Las horas críticas se pintan de un color de ESTADO, reservado para ellas.
const COLOR_HC = "#ea580c";
const COLOR_UMBRAL = "#0d9488"; // HC #N: su línea punteada y su serie/leyenda
const COLOR_MIN = "#7c3aed"; // HC #1: su línea punteada y su serie/leyenda
const MESES_G = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];

type Medida = "pct" | "mw";

const num = (n: number | null | undefined, d = 2) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("es-MX", { minimumFractionDigits: d, maximumFractionDigits: d }).format(n);
const pct = (n: number | null | undefined, d = 2) => (n == null ? "—" : `${num(n, d)} %`);
const fmtFecha = (iso: string) => {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

// Hora del año (1 = 1-ene hora 1) a partir de la fecha ISO y la hora 1–24.
const diaDelAnio = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number);
  return Math.round((Date.UTC(y, m - 1, d) - Date.UTC(y, 0, 1)) / 86_400_000);
};
const horaDelAnio = (iso: string, h: number) => diaDelAnio(iso) * 24 + h;
const horasDelAnio = (anio: number) =>
  Math.round((Date.UTC(anio + 1, 0, 1) - Date.UTC(anio, 0, 1)) / 3_600_000);

type Punto = DispersionHcFila & { x: number };

// Marcador de HC 1 / HC N: rombo relleno con aro blanco, para distinguirse de
// los círculos de la nube y de las HC. Lo usan la gráfica y la leyenda.
function MarcaHc({ cx, cy, color }: { cx: number; cy: number; color: string }) {
  const r = 6;
  return (
    <polygon
      points={`${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`}
      fill={color}
      stroke="#ffffff"
      strokeWidth={1.5}
    />
  );
}

/** Cruz: la hora coincide con una HC DEFINITIVA publicada por CENACE. */
function MarcaCruz({ cx, cy, color, r = 4 }: { cx: number; cy: number; color: string; r?: number }) {
  return (
    <g stroke={color} strokeWidth={1.8} strokeLinecap="round">
      <line x1={cx - r} y1={cy - r} x2={cx + r} y2={cy + r} />
      <line x1={cx - r} y1={cy + r} x2={cx + r} y2={cy - r} />
    </g>
  );
}

/** La misma cruz de la gráfica, en tamaño de texto para usarla en tablas. */
function IconoCruz({ color, size = 11 }: { color: string; size?: number }) {
  const m = size / 2;
  const r = size * 0.32;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="inline-block" aria-hidden>
      <g stroke={color} strokeWidth={1.8} strokeLinecap="round">
        <line x1={m - r} y1={m - r} x2={m + r} y2={m + r} />
        <line x1={m - r} y1={m + r} x2={m + r} y2={m - r} />
      </g>
    </svg>
  );
}

// Dispersión de horas críticas (HC): TODAS las horas del año como nube (x = hora
// del año, y = margen de reserva) y, resaltadas, las N de menor % de margen
// dentro del periodo HC que define CENACE para el cálculo de potencia.
export function DispersionHcPage() {
  const [anio, setAnio] = useState(0);
  const [sistemas, setSistemas] = useState<string[]>(["SIN"]);
  const [top, setTop] = useState(100);
  const [intercambio, setIntercambio] = useState(false);
  const [medida, setMedida] = useState<Medida>("pct");

  const rangoQ = useQuery({
    queryKey: ["ofertas-capacidad-rango"],
    queryFn: getOfertasCapacidadRango,
    staleTime: 60 * 60_000,
  });
  const anioMin = rangoQ.data?.mes_ini ? Number(rangoQ.data.mes_ini.slice(0, 4)) : null;
  const anioMax = rangoQ.data?.mes_fin ? Number(rangoQ.data.mes_fin.slice(0, 4)) : null;
  const anios =
    anioMin != null && anioMax != null
      ? Array.from({ length: anioMax - anioMin + 1 }, (_, i) => anioMax - i)
      : [];
  useEffect(() => {
    if (anioMax != null) setAnio((a) => (a ? a : anioMax));
  }, [anioMax]);
  // Periodo HC del año (informativo): lo captura el admin en Configuración →
  // Periodo de horas críticas; aquí solo se muestra y el backend lo aplica.
  const periodoQ = useQuery({
    queryKey: ["periodo-hc", anio],
    queryFn: () => getPeriodoHc(anio),
    enabled: anio > 0,
    staleTime: 5 * 60_000,
  });
  const periodo = periodoQ.data;

  // La consulta agrega un año completo (~18 s): solo al presionar Buscar.
  const [aplicado, setAplicado] = useState<{
    anio: number;
    sistemas: string[];
    top: number;
    intercambio: boolean;
  } | null>(null);

  const q = useQuery({
    queryKey: [
      "dispersion-hc",
      aplicado?.anio,
      aplicado?.sistemas.join(","),
      aplicado?.top,
      aplicado?.intercambio,
    ],
    queryFn: () =>
      getDispersionHc(
        aplicado!.anio, aplicado!.sistemas, aplicado!.top, aplicado!.intercambio,
      ),
    enabled: !!aplicado,
    retry: false,
    staleTime: 30 * 60_000,
  });
  const data = q.data;
  const filas = data?.filas ?? [];

  const filtrosListos = sistemas.length > 0 && anio > 0;
  const filtrosCambiaron =
    !!aplicado &&
    (aplicado.anio !== anio ||
      aplicado.top !== top ||
      aplicado.intercambio !== intercambio ||
      aplicado.sistemas.join(",") !== sistemas.join(","));

  const buscar = () => {
    if (!filtrosListos) return;
    setAplicado({ anio, sistemas: [...sistemas], top, intercambio });
  };
  const toggleSistema = (s: string) =>
    setSistemas((prev) =>
      prev.includes(s) ? (prev.length > 1 ? prev.filter((x) => x !== s) : prev) : [...prev, s],
    );

  // Una gráfica por sistema: sus escalas son muy distintas (SIN ~50 GW, BCA ~2 GW)
  // y mezclarlas en un eje escondería la dispersión de los chicos.
  const porSistema = useMemo(() => {
    const m = new Map<string, { nube: Punto[]; hc: Punto[] }>();
    for (const f of filas) {
      const p: Punto = { ...f, x: horaDelAnio(f.f, f.h) };
      const g = m.get(f.s) ?? { nube: [], hc: [] };
      (f.hc != null ? g.hc : g.nube).push(p);
      m.set(f.s, g);
    }
    for (const g of m.values()) g.hc.sort((a, b) => (a.hc ?? 0) - (b.hc ?? 0));
    return m;
  }, [filas]);

  const anioG = data?.anio ?? 0;
  const ejeX = useMemo(() => {
    if (!anioG) return { max: 1, ticks: [] as number[] };
    return {
      max: horasDelAnio(anioG),
      ticks: MESES_G.map((_, i) => horaDelAnio(`${anioG}-${String(i + 1).padStart(2, "0")}-01`, 1)),
    };
  }, [anioG]);
  // CENACE define la ventana HC por sistema, así que cada gráfica tiene la suya.
  const bandaDe = (s: string): [number, number] | null => {
    const v = data?.periodo.sistemas?.[s];
    if (!v) return null;
    const [d0, d1] = zoomX[s] ?? [1, Number.MAX_SAFE_INTEGER];
    const a = Math.max(horaDelAnio(v.desde, 1), d0);
    const b = Math.min(horaDelAnio(v.hasta, 24), d1);
    return a <= b ? [a, b] : null;   // fuera del tramo visible
  };

  const [validar, setValidar] = useState(false);
  const hayReales = data?.hay_reales ?? false;
  // Si el año aún no lo publica CENACE, la validación no aplica.
  const validando = validar && hayReales;

  const valor = (p: Punto) => (medida === "pct" ? p.pct : p.mw);
  const etiquetaY = medida === "pct" ? "Margen de reserva (%)" : "Margen capacidad − demanda (MW)";

  // Listado de HC de todos los sistemas (para la tabla y el CSV), por sistema y #.
  const listaHC = useMemo(
    () => [...porSistema.entries()].flatMap(([, g]) => g.hc),
    [porSistema],
  );

  // HC que CENACE marcó y nuestro top-N NO alcanzó: están en la nube (hcr con
  // número, hc vacío). Se listan aparte, debajo de las nuestras, en rojo.
  const listaFaltantes = useMemo(
    () =>
      filas
        .filter((f) => f.hcr != null && f.hc == null)
        .sort((a, b) => a.s.localeCompare(b.s) || (a.hcr ?? 0) - (b.hcr ?? 0)),
    [filas],
  );

  // ---- Orden de la tabla: clic en el encabezado (asc → desc → original) ----
  const [orden, setOrden] = useState<{ col: keyof DispersionHcFila; dir: "asc" | "desc" } | null>(null);
  const clickCol = (col: keyof DispersionHcFila) =>
    setOrden((o) =>
      o?.col !== col ? { col, dir: "asc" } : o.dir === "asc" ? { col, dir: "desc" } : null,
    );
  /** Ordena sin mezclar los dos bloques: las nuestras y las de CENACE que
   *  faltaron se ordenan por separado y las segundas siguen al final. */
  const ordenar = <T extends DispersionHcFila>(lista: T[]): T[] => {
    if (!orden) return lista;
    const k = orden.dir === "asc" ? 1 : -1;
    return [...lista].sort((a, b) => {
      const va = a[orden.col] as number | string | null;
      const vb = b[orden.col] as number | string | null;
      if (va == null && vb == null) return 0;
      if (va == null) return 1;   // los vacíos siempre al final
      if (vb == null) return -1;
      return (typeof va === "string" || typeof vb === "string"
        ? String(va).localeCompare(String(vb))
        : va - vb) * k;
    });
  };
  const thOrden = (col: keyof DispersionHcFila, texto: string, clase = "", titulo?: string) => (
    <th
      className={`px-3 py-2 cursor-pointer select-none hover:text-slate-900 ${clase}`}
      onClick={() => clickCol(col)}
      title={titulo ?? `Ordenar por ${texto}`}
    >
      {texto}
      <span className="ml-1 text-[9px] text-slate-400">
        {orden?.col === col ? (orden.dir === "asc" ? "▲" : "▼") : "↕"}
      </span>
    </th>
  );

  // ---- Zoom del eje X con la rueda del mouse, independiente por sistema ----
  // El eje X son horas del año (1 … 8760). Guardamos el tramo visible de cada
  // gráfica; sin entrada = año completo.
  const [zoomX, setZoomX] = useState<Record<string, [number, number]>>({});
  // Los listeners de rueda se registran una sola vez por elemento, así que leen
  // el estado por referencia para no quedarse con un valor viejo.
  const zoomRef = useRef(zoomX);
  zoomRef.current = zoomX;
  const maxRef = useRef(1);
  const refsZoom = useRef<Record<string, (el: HTMLDivElement | null) => void>>({});
  const limpiar = useRef<Record<string, () => void>>({});

  const ANCHO_MIN = 24; // no acercarse más de un día

  const getRefZoom = (s: string) => {
    if (!refsZoom.current[s]) {
      refsZoom.current[s] = (el: HTMLDivElement | null) => {
        limpiar.current[s]?.();
        delete limpiar.current[s];
        if (!el) return;
        const onWheel = (ev: WheelEvent) => {
          const max = maxRef.current;
          if (max <= 1) return;
          ev.preventDefault(); // si no, la página se desplaza al hacer zoom
          const r = el.getBoundingClientRect();
          const izq = 76, der = 24; // ancho del eje Y y margen derecho del chart
          const ancho = r.width - izq - der;
          if (ancho <= 0) return;
          const frac = Math.min(1, Math.max(0, (ev.clientX - r.left - izq) / ancho));
          const [a, b] = zoomRef.current[s] ?? [1, max];
          const cursor = a + frac * (b - a);
          const nuevo = Math.min(max - 1, Math.max(ANCHO_MIN, (b - a) * (ev.deltaY < 0 ? 0.8 : 1.25)));
          let na = cursor - frac * nuevo;
          let nb = na + nuevo;
          if (na < 1) { na = 1; nb = 1 + nuevo; }
          if (nb > max) { nb = max; na = max - nuevo; }
          setZoomX((prev) =>
            nb - na >= max - 1
              ? Object.fromEntries(Object.entries(prev).filter(([k]) => k !== s))
              : { ...prev, [s]: [Math.round(na), Math.round(nb)] },
          );
        };
        el.addEventListener("wheel", onWheel, { passive: false });
        limpiar.current[s] = () => el.removeEventListener("wheel", onWheel);
      };
    }
    return refsZoom.current[s];
  };

  useEffect(() => () => { Object.values(limpiar.current).forEach((f) => f()); }, []);
  // Cada consulta nueva vuelve al año completo.
  useEffect(() => { setZoomX({}); }, [data]);

  maxRef.current = ejeX.max;
  const dominioX = (s: string): [number, number] => zoomX[s] ?? [1, ejeX.max];
  const fechaDeHora = (h: number) => {
    const d = new Date(Date.UTC(anioG, 0, 1));
    d.setUTCDate(d.getUTCDate() + Math.floor((h - 1) / 24));
    return d.toISOString().slice(0, 10);
  };

  // Acierto total: de las HC reales de los sistemas elegidos, cuántas caímos.
  const aciertos = useMemo(() => {
    const v = data?.validacion ?? {};
    return sistemas.reduce(
      (acc, s) => ({
        reales: acc.reales + (v[s]?.reales ?? 0),
        coinciden: acc.coinciden + (v[s]?.coinciden ?? 0),
      }),
      { reales: 0, coinciden: 0 },
    );
  }, [data, sistemas]);

  const descargarCsv = () => {
    if (!data || listaHC.length === 0) return;
    const enc = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const cab = ["#HC", "Sistema", "Fecha", "Hora", "Margen (MW)", "Margen de reserva (%)",
      "Capacidad ofertada (MW)", ...(data.intercambio ? ["+Import − export (MW)"] : []),
      "Demanda real (MW)", "Fuente demanda", "#HC real CENACE", "Nota"];
    const lineas = [...listaHC, ...(validando ? listaFaltantes : [])].map((p) => [
      p.hc, p.s, p.f, p.h, p.mw?.toFixed(3), p.pct?.toFixed(4), p.cap?.toFixed(3),
      ...(data.intercambio ? [p.int?.toFixed(3) ?? ""] : []), p.dem?.toFixed(3), p.fd,
      p.hcr ?? "",
      p.hc == null ? "CENACE, fuera de nuestro top" : "",
    ].map(enc).join(","));
    const csv = "﻿" + [cab.map(enc).join(","), ...lineas].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dispersion_hc_top${data.top}_${data.sistemas.join("-")}_${data.anio}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <ScatterIcon className="w-5 h-5 text-slate-500" /> Dispersión de Horas Críticas (HC)
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Cada punto es <b>una hora del año</b>: horizontal = hora del año (con los meses como
          referencia), vertical = <b>margen de reserva</b> (capacidad total ofertada al MTR menos
          la demanda real, en % de la capacidad o en MW). Las <b>horas críticas</b> son las{" "}
          {top} horas con <b>menor % de margen de reserva</b> dentro del periodo que CENACE
          define cada año para el cálculo de potencia (se captura en Reportes → Periodo de
          horas críticas); se resaltan en naranja y la banda sombreada marca el periodo. Misma fuente y mismo cálculo que{" "}
          <b>Margen capacidad − demanda</b>; CENACE publica las ofertas al MTR con ~60 días de
          rezago, por eso la nube llega hasta ~2 meses antes de hoy.
        </p>
      </div>

      <div className="bg-white border rounded-lg px-4 py-3 flex flex-wrap items-end gap-4">
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Año</span>
          <select
            className="mt-1 border rounded px-2 py-1.5 text-sm"
            value={anio || ""}
            onChange={(e) => setAnio(Number(e.target.value))}
            disabled={anios.length === 0}
          >
            {anios.length === 0 && <option value="">…</option>}
            {anios.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </label>
        <div>
          <span className="block text-xs font-medium text-slate-600 mb-1">Sistema</span>
          <div className="flex gap-1.5">
            {SISTEMAS.map((s) => {
              const on = sistemas.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleSistema(s)}
                  className={`text-sm border rounded px-3 py-1.5 ${
                    on ? "bg-blue-600 text-white border-blue-600" : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {s}
                </button>
              );
            })}
          </div>
        </div>
        <div
          className="block"
          title="Ventana de CENACE para las horas críticas del año. Se captura en Reportes → Periodo de horas críticas"
        >
          <span className="text-xs font-medium text-slate-600">Periodo HC {anio || ""} (CENACE)</span>
          <div className="mt-1 inline-flex items-center gap-1.5 border border-dashed border-amber-400 bg-amber-50 text-amber-900 rounded px-2.5 py-1.5 text-sm tabular-nums">
            <CalendarRange className="w-4 h-4 text-amber-600" />
            {periodoQ.isLoading ? (
              "…"
            ) : sistemas.some((s) => periodo?.sistemas?.[s]) ? (
              <span className="flex flex-col gap-0.5">
                {sistemas.map((s) =>
                  periodo?.sistemas?.[s] ? (
                    <span key={s}>
                      <b className="font-medium">{s}</b>{" "}
                      {fmtFecha(periodo.sistemas[s].desde)} a {fmtFecha(periodo.sistemas[s].hasta)}
                    </span>
                  ) : (
                    <span key={s} className="text-amber-700 italic">
                      {s} sin periodo
                    </span>
                  ),
                )}
                {periodo?.fuente === "default" && (
                  <span className="text-[10px] text-amber-700 italic">(respaldo, sin capturar)</span>
                )}
              </span>
            ) : (
              <span className="italic">sin periodo capturado para {anio}</span>
            )}
          </div>
        </div>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Horas críticas</span>
          <select
            className="mt-1 block border rounded px-2 py-1.5 text-sm"
            value={top}
            onChange={(e) => setTop(Number(e.target.value))}
          >
            {[25, 50, 100, 200, 500].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>
        <div>
          <span className="block text-xs font-medium text-slate-600 mb-1">Eje Y</span>
          <div className="flex gap-1.5">
            {(["pct", "mw"] as Medida[]).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMedida(m)}
                className={`text-sm border rounded px-3 py-1.5 ${
                  medida === m ? "bg-slate-700 text-white border-slate-700" : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                {m === "pct" ? "% margen" : "MW"}
              </button>
            ))}
          </div>
        </div>
        <label
          className="flex items-center gap-2 text-sm text-slate-600 pb-2 cursor-pointer select-none"
          title="Suma a la capacidad el intercambio neto del balance (la oferta al MTR no incluye importaciones)"
        >
          <input
            type="checkbox"
            className="w-4 h-4 accent-blue-600"
            checked={intercambio}
            onChange={(e) => setIntercambio(e.target.checked)}
          />
          Incluir +importaciones − exportaciones
        </label>
        <label
          className="flex items-center gap-2 text-sm pb-2 cursor-pointer select-none"
          title={
            hayReales
              ? "Marca con una cruz las horas que CENACE publicó como críticas definitivas (no las preliminares)"
              : "CENACE aún no publica las horas críticas definitivas de este año"
          }
        >
          <input
            type="checkbox"
            className="w-4 h-4 accent-blue-600"
            checked={validar}
            disabled={!hayReales}
            onChange={(e) => setValidar(e.target.checked)}
          />
          <span className={hayReales ? "text-slate-600" : "text-slate-400"}>
            Validación con Horas Críticas Reales
          </span>
        </label>
        <button
          type="button"
          onClick={buscar}
          disabled={!filtrosListos || q.isFetching}
          title={
            filtrosListos
              ? undefined
              : "Elige el año y al menos un sistema"
          }
          className="inline-flex items-center gap-2 bg-blue-600 text-white rounded px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Search className="w-4 h-4" /> {q.isFetching ? "Buscando…" : "Buscar"}
        </button>
        <button
          type="button"
          onClick={descargarCsv}
          disabled={listaHC.length === 0}
          title={aplicado ? "Descarga la lista de horas críticas del corte buscado" : "Primero presiona Buscar"}
          className="inline-flex items-center gap-2 bg-emerald-600 text-white rounded px-4 py-2 text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="w-4 h-4" /> Descargar HC (CSV)
        </button>
        <span
          className="text-[11px] text-slate-400 pb-2 ml-auto"
          title="CENACE publica las ofertas al MTR con ~60 días de rezago; el ETL descarga todo lo ya publicado"
        >
          {q.isFetching
            ? "Cargando (agrega el año completo, ~20 s)…"
            : aplicado
              ? `${filas.length.toLocaleString("es-MX")} horas`
              : "sin buscar"}
          {rangoQ.data?.mes_ini &&
            ` · capacidad disponible ${rangoQ.data.mes_ini} a ${rangoQ.data.mes_fin}`}
        </span>
      </div>

      {filtrosCambiaron && !q.isFetching && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded p-3 text-sm">
          Cambiaste los filtros — se sigue mostrando {aplicado!.anio} ({aplicado!.sistemas.join(", ")}
          ), top {aplicado!.top}. Presiona <b>Buscar</b> para actualizar.
        </div>
      )}
      {q.isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          {(q.error as Error)?.message ?? "No se pudo consultar el reporte."}
        </div>
      )}
      {periodo && anio > 0 && !sistemas.some((s) => periodo.sistemas?.[s]) && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 text-sm">
          No hay periodo de horas críticas capturado para {anio} en los sistemas elegidos: la nube
          se dibuja, pero no se marcará ninguna HC. Captúralo en{" "}
          <Link to="/periodo-hc" className="underline font-medium">Periodo de horas críticas</Link>.
        </div>
      )}
      {data && filas.length === 0 && (
        <div className="bg-slate-50 border rounded p-3 text-sm text-slate-600">
          Sin horas con capacidad y demanda para {data.anio} en {data.sistemas.join(", ")}.
        </div>
      )}

      {[...porSistema.entries()].map(([s, g]) => {
        const hc1 = g.hc[0];
        const hcN = g.hc[g.hc.length - 1];
        const yMin = hc1 ? valor(hc1) : null;
        const yUmbral = hcN ? valor(hcN) : null;
        const hayNeg = [...g.nube, ...g.hc].some((p) => (valor(p) ?? 0) < 0);
        const color = COLOR_SISTEMA[s] ?? "#64748b";
        const etiquetaHc1 = hc1 ? `HC 1: ${fmtFecha(hc1.f)}, hora ${hc1.h}` : "";
        const etiquetaHcN = hcN ? `HC ${hcN.hc}: ${fmtFecha(hcN.f)}, hora ${hcN.h}` : "";
        // Dominio Y con un poco de aire arriba y abajo para que los rombos de
        // HC 1 / HC N no queden cortados en el borde.
        const vals = [...g.nube, ...g.hc].map(valor).filter((v): v is number => v != null);
        const vMin = Math.min(...vals);
        const vMax = Math.max(...vals);
        const dominioY: [number, number] =
          vals.length === 0 ? [0, 1] : [vMin - (vMax - vMin) * 0.06, vMax + (vMax - vMin) * 0.03];
        return (
          <div key={s} className="bg-white border rounded-lg p-4">
            <div className="mb-2">
              <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                Horas Críticas (HC) — {s} · {anioG} · top {data?.top}
              </h3>
              {validando && data?.validacion?.[s] && (
                <p className="text-[11px] mt-0.5">
                  <span className="text-emerald-700 font-medium">
                    <IconoCruz color="#047857" /> Acertó {data.validacion[s].coinciden} de{" "}
                    {data.validacion[s].reales} horas críticas reales
                    {data.validacion[s].reales > 0 && (
                      <>
                        {" "}(
                        {Math.round(
                          (data.validacion[s].coinciden / data.validacion[s].reales) * 100,
                        )}
                        %)
                      </>
                    )}
                  </span>
                  <span className="text-slate-500">
                    {data.validacion[s].fuera > 0 && (
                      <>{" · "}{data.validacion[s].fuera} reales quedaron fuera del top</>
                    )}
                  </span>
                </p>
              )}
              <p className="text-[11px] text-slate-500">
                {zoomX[s] && (
                  <button
                    type="button"
                    onClick={() => setZoomX((p) => Object.fromEntries(Object.entries(p).filter(([k]) => k !== s)))}
                    className="mr-2 text-blue-700 hover:underline"
                  >
                    {fmtFecha(fechaDeHora(zoomX[s][0]))} a {fmtFecha(fechaDeHora(zoomX[s][1]))} · ver año completo
                  </button>
                )}
                {g.nube.length + g.hc.length} horas con datos
                {hc1 && hcN ? (
                  <>
                    {" · "}{g.hc.length} horas críticas
                    {data!.periodo.sistemas?.[s] && (
                      <>
                        {" · "}periodo HC {fmtFecha(data!.periodo.sistemas[s].desde)} a{" "}
                        {fmtFecha(data!.periodo.sistemas[s].hasta)}
                      </>
                    )}
                  </>
                ) : (
                  <> · sin horas dentro del periodo HC todavía</>
                )}
              </p>
            </div>
            <div
              className="h-[420px]"
              ref={getRefZoom(s)}
              onDoubleClick={() => setZoomX((p) => Object.fromEntries(Object.entries(p).filter(([k]) => k !== s)))}
              title="Rueda del mouse para acercar; doble clic para ver el año completo"
            >
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 10, right: 24, left: 0, bottom: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="x"
                    type="number"
                    domain={dominioX(s)}
                    allowDataOverflow
                    ticks={ejeX.ticks.filter(
                      (t) => t >= dominioX(s)[0] && t <= dominioX(s)[1],
                    )}
                    tickFormatter={(v) => {
                      const i = ejeX.ticks.indexOf(Number(v));
                      return i >= 0 ? MESES_G[i] : "";
                    }}
                    tick={{ fontSize: 11, fill: "#475569" }}
                    label={{
                      value: "Hora del año",
                      position: "insideBottom",
                      offset: -10,
                      style: { fontSize: 11, fill: "#64748b" },
                    }}
                  />
                  <YAxis
                    dataKey={medida}
                    type="number"
                    domain={dominioY}
                    allowDataOverflow
                    tick={{ fontSize: 11, fill: "#475569" }}
                    width={76}
                    tickFormatter={(v) =>
                      medida === "pct"
                        ? `${new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0 }).format(Number(v))} %`
                        : new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0 }).format(Number(v))
                    }
                    label={{
                      value: etiquetaY,
                      angle: -90,
                      position: "insideLeft",
                      style: { fontSize: 11, fill: "#64748b" },
                    }}
                  />
                  {/* Periodo HC de CENACE (banda sombreada) */}
                  {bandaDe(s) && (
                    <ReferenceArea
                      x1={bandaDe(s)![0]}
                      x2={bandaDe(s)![1]}
                      fill="#f59e0b"
                      fillOpacity={0.07}
                      stroke="#f59e0b"
                      strokeOpacity={0.35}
                      strokeDasharray="4 3"
                    />
                  )}
                  {hayNeg && (
                    <ReferenceLine y={0} stroke="#dc2626" strokeDasharray="5 3" strokeWidth={1.5} />
                  )}
                  {yUmbral != null && (
                    <ReferenceLine
                      y={yUmbral}
                      stroke={COLOR_UMBRAL}
                      strokeDasharray="6 4"
                      strokeWidth={1.5}
                    />
                  )}
                  {yMin != null && (
                    <ReferenceLine
                      y={yMin}
                      stroke={COLOR_MIN}
                      strokeDasharray="6 4"
                      strokeWidth={1.5}
                    />
                  )}
                  <Tooltip
                    cursor={{ strokeDasharray: "3 3" }}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const p = payload[0].payload as Punto;
                      return (
                        <div className="bg-white border rounded shadow px-3 py-2 text-xs min-w-[220px]">
                          <div className="font-semibold mb-1">
                            {p.hc != null ? (
                              <span style={{ color: COLOR_HC }}>HC #{p.hc} · {p.s}</span>
                            ) : (
                              <span>{p.s}</span>
                            )}
                            {validando && p.hcr != null && (
                              <span className="ml-2 text-emerald-700">
                                ✕ HC real CENACE #{p.hcr}
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5">
                            <span className="text-slate-500">Fecha y hora</span>
                            <span className="text-right tabular-nums">
                              {fmtFecha(p.f)} · {String(p.h).padStart(2, "0")}:00 h (hora {p.h})
                            </span>
                            <span className="text-slate-500">% margen de reserva</span>
                            <b className={`text-right tabular-nums ${(p.pct ?? 0) < 0 ? "text-red-700" : ""}`}>
                              {pct(p.pct)}
                            </b>
                            <span className="text-slate-500">Margen</span>
                            <b className={`text-right tabular-nums ${p.mw < 0 ? "text-red-700" : ""}`}>
                              {num(p.mw, 0)} MW
                            </b>
                            <span className="text-slate-500">Capacidad ofertada</span>
                            <span className="text-right tabular-nums">{num(p.cap, 0)} MW</span>
                            {p.int != null && (
                              <>
                                <span className="text-slate-500">+Import − export</span>
                                <span className="text-right tabular-nums">{num(p.int, 0)} MW</span>
                              </>
                            )}
                            <span className="text-slate-500">Demanda real</span>
                            <span className="text-right tabular-nums">
                              {num(p.dem, 0)} MW <span className="text-slate-400">({p.fd})</span>
                            </span>
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Scatter
                    name={`${s} · horas`}
                    data={g.nube}
                    fill={color}
                    fillOpacity={0.55}
                    isAnimationActive={false}
                    shape={(props: { cx?: number; cy?: number; payload?: Punto }) =>
                      props.cx == null || props.cy == null || Number.isNaN(props.cy) ? (
                        <g />
                      ) : validando && props.payload?.hcr != null ? (
                        <MarcaCruz cx={props.cx} cy={props.cy} color={color} r={3.2} />
                      ) : (
                        <circle cx={props.cx} cy={props.cy} r={2.2} fill={color} fillOpacity={0.55} />
                      )
                    }
                  />
                  <Scatter
                    name={`${s} · horas críticas`}
                    data={g.hc}
                    fill={COLOR_HC}
                    isAnimationActive={false}
                    shape={(props: { cx?: number; cy?: number; payload?: Punto }) =>
                      props.cx == null || props.cy == null || Number.isNaN(props.cy) ? (
                        <g />
                      ) : validando && props.payload?.hcr != null ? (
                        <MarcaCruz cx={props.cx} cy={props.cy} color={COLOR_HC} r={4.5} />
                      ) : (
                        <circle cx={props.cx} cy={props.cy} r={3.2} fill={COLOR_HC} stroke="#fff" strokeWidth={0.8} />
                      )
                    }
                  />
                  {/* HC 1 y HC N como series propias (un punto cada una) para que
                      aparezcan en la leyenda con su fecha y hora. */}
                  {hc1 && (
                    <Scatter
                      name={etiquetaHc1}
                      data={[hc1]}
                      fill={COLOR_MIN}
                      isAnimationActive={false}
                      shape={(props: { cx?: number; cy?: number }) =>
                        props.cx == null || props.cy == null || Number.isNaN(props.cy) ? (
                          <g />
                        ) : (
                          <MarcaHc cx={props.cx} cy={props.cy} color={COLOR_MIN} />
                        )
                      }
                    />
                  )}
                  {hcN && hcN !== hc1 && (
                    <Scatter
                      name={etiquetaHcN}
                      data={[hcN]}
                      fill={COLOR_UMBRAL}
                      isAnimationActive={false}
                      shape={(props: { cx?: number; cy?: number }) =>
                        props.cx == null || props.cy == null || Number.isNaN(props.cy) ? (
                          <g />
                        ) : (
                          <MarcaHc cx={props.cx} cy={props.cy} color={COLOR_UMBRAL} />
                        )
                      }
                    />
                  )}
                </ScatterChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-4 justify-center text-xs text-slate-600 mt-1">
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color, opacity: 0.7 }} />
                Horas del año
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: COLOR_HC }} />
                Horas críticas (menor % de margen en el periodo)
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-4 h-2.5 border border-dashed border-amber-500 bg-amber-500/10" />
                Periodo HC CENACE
              </span>
              {hc1 && (
                <span className="inline-flex items-center gap-1.5 italic" style={{ color: COLOR_MIN }}>
                  <svg width="14" height="14" viewBox="-7 -7 14 14"><MarcaHc cx={0} cy={0} color={COLOR_MIN} /></svg>
                  {etiquetaHc1}
                </span>
              )}
              {hcN && hcN !== hc1 && (
                <span className="inline-flex items-center gap-1.5 italic" style={{ color: COLOR_UMBRAL }}>
                  <svg width="14" height="14" viewBox="-7 -7 14 14"><MarcaHc cx={0} cy={0} color={COLOR_UMBRAL} /></svg>
                  {etiquetaHcN}
                </span>
              )}
            </div>
          </div>
        );
      })}

      {listaHC.length > 0 && (
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b">
            <h3 className="text-sm font-semibold text-slate-800">
              Horas críticas — {listaHC.length} en total
              {validando && (
                <>
                  <span className="ml-2 font-normal text-emerald-700">
                    <IconoCruz color="#047857" /> Acertó {aciertos.coinciden} de {aciertos.reales}{" "}
                    horas críticas reales
                    {aciertos.reales > 0 && (
                      <> ({Math.round((aciertos.coinciden / aciertos.reales) * 100)}%)</>
                    )}
                  </span>
                  {listaFaltantes.length > 0 && (
                    <span className="ml-2 font-normal text-red-700">
                      · {listaFaltantes.length} de CENACE que no salieron
                    </span>
                  )}
                </>
              )}
            </h3>
            <p className="text-[11px] text-slate-500">
              Ordenadas por sistema y de la más apretada (#1) a la menos, por % de margen de
              reserva dentro del periodo HC. Clic en una columna para reordenar.
              {validando &&
                " La cruz marca las que están en la lista definitiva de CENACE. Al final, en rojo, van las que CENACE sí marcó y nuestro top no alcanzó."}
            </p>
          </div>
          <div className="overflow-auto max-h-[520px]">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-left text-slate-600">
                  {thOrden("hc", "#HC", "text-right")}
                  {validando &&
                    thOrden("hcr", "HC real CENACE", "text-center",
                      "Número de hora crítica en la lista definitiva de CENACE")}
                  {thOrden("s", "Sistema")}
                  {thOrden("f", "Fecha")}
                  {thOrden("h", "Hora", "text-right",
                    "Hora 1–24: la hora h cubre de (h−1):00 a h:00")}
                  {thOrden("pct", "Margen de reserva (%)", "text-right")}
                  {thOrden("mw", "Margen (MW)", "text-right")}
                  {thOrden("cap", "Capacidad ofertada (MW)", "text-right")}
                  {data?.intercambio && thOrden("int", "+Import − export (MW)", "text-right")}
                  {thOrden("dem", "Demanda real (MW)", "text-right")}
                </tr>
              </thead>
              <tbody>
                {ordenar(listaHC).map((p) => (
                  <tr key={`${p.s}|${p.f}|${p.h}`} className="border-t hover:bg-slate-50">
                    <td className="px-3 py-1.5 text-right tabular-nums font-medium" style={{ color: COLOR_HC }}>
                      {p.hc}
                    </td>
                    {validando && (
                      <td className="px-3 py-1.5 text-center tabular-nums whitespace-nowrap">
                        {p.hcr != null ? (
                          <span className="text-emerald-700 font-medium">
                            <IconoCruz color="#047857" /> #{p.hcr}
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-1.5">{p.s}</td>
                    <td className="px-3 py-1.5 tabular-nums">{fmtFecha(p.f)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{p.h}</td>
                    <td className={`px-3 py-1.5 text-right tabular-nums ${(p.pct ?? 0) < 0 ? "text-red-700" : ""}`}>
                      {pct(p.pct)}
                    </td>
                    <td className={`px-3 py-1.5 text-right tabular-nums ${p.mw < 0 ? "text-red-700" : ""}`}>
                      {num(p.mw, 0)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{num(p.cap, 0)}</td>
                    {data?.intercambio && (
                      <td className="px-3 py-1.5 text-right tabular-nums">{num(p.int, 0)}</td>
                    )}
                    <td className="px-3 py-1.5 text-right tabular-nums">{num(p.dem, 0)}</td>
                  </tr>
                ))}
                {validando &&
                  ordenar(listaFaltantes).map((p) => (
                    <tr
                      key={`falta|${p.s}|${p.f}|${p.h}`}
                      className="border-t bg-red-50 hover:bg-red-100"
                      title="CENACE la marcó como hora crítica y nuestro top-N no la alcanzó"
                    >
                      <td className="px-3 py-1.5 text-right text-red-400">—</td>
                      <td className="px-3 py-1.5 text-center tabular-nums whitespace-nowrap">
                        <span className="text-red-700 font-medium">
                          <IconoCruz color="#b91c1c" /> #{p.hcr}
                        </span>
                      </td>
                      <td className="px-3 py-1.5">{p.s}</td>
                      <td className="px-3 py-1.5 tabular-nums">{fmtFecha(p.f)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{p.h}</td>
                      <td className={`px-3 py-1.5 text-right tabular-nums ${(p.pct ?? 0) < 0 ? "text-red-700" : ""}`}>
                        {pct(p.pct)}
                      </td>
                      <td className={`px-3 py-1.5 text-right tabular-nums ${p.mw < 0 ? "text-red-700" : ""}`}>
                        {num(p.mw, 0)}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{num(p.cap, 0)}</td>
                      {data?.intercambio && (
                        <td className="px-3 py-1.5 text-right tabular-nums">{num(p.int, 0)}</td>
                      )}
                      <td className="px-3 py-1.5 text-right tabular-nums">{num(p.dem, 0)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
